import { InlineEditItem } from '@amzn/awscat-pattern-library-service-model';
import { useEffect, useMemo, useState } from 'react';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { stringifyDocId } from '../../model/RichtextDocId';
import { InlineTextFilter } from '../../services/InlineTextOperations';
import { useOpenInlineEdits } from './useOpenInlineEdits';

function singleKey(filter: InlineTextFilter): string {
  if (filter.type === 'none') return 'none';
  return stringifyDocId(filter);
}

function computeKey(filter: InlineTextFilter | readonly InlineTextFilter[]): string {
  if (Array.isArray(filter)) {
    return (filter as readonly InlineTextFilter[]).map(singleKey).join('|');
  }
  return singleKey(filter as InlineTextFilter);
}

/**
 * Filters inline edits for one or more content regions, automatically recomputing when edits or node mappings change.
 */
export function useFilteredInlineEdits(
  filter: InlineTextFilter | readonly InlineTextFilter[],
): readonly InlineEditItem[] {
  const service = useContentEditorService();
  const openedEdits = useOpenInlineEdits();
  const key = computeKey(filter);

  // Bump a revision counter when ContentEditorService emits 'changed' so
  // the downstream useMemo recomputes (node ID mappings may have shifted).
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    return service.on('changed', () => setRevision((r) => r + 1));
  }, [service]);

  return useMemo(() => {
    if (!openedEdits) return [];
    const filters = Array.isArray(filter) ? filter : [filter];
    const seen = new Set<string>();
    const results: InlineEditItem[] = [];

    for (const f of filters as readonly InlineTextFilter[]) {
      for (const edit of service.filterInlineEdits(openedEdits.edits, f)) {
        if (edit.editId && seen.has(edit.editId)) continue;
        if (edit.editId) seen.add(edit.editId);
        results.push(edit);
      }
    }

    return results;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedEdits, service, key, revision]);
}
